import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { profileAPI, IMG_URL } from '../utils/api';
import { Card, Row, Col, Typography, Spin, Avatar, Space, Descriptions, Button, Upload, message, Tag } from 'antd';
import { UserOutlined, HomeOutlined, CalendarOutlined, EditOutlined, UploadOutlined } from '@ant-design/icons';
import useDocumentTitle from '../hooks/useDocumentTitle';

const { Title, Text, Paragraph } = Typography;

// 格式化日期时间
const formatDate = (dateString) => {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return dateString;
  }
};

const Profile = () => {
  const { username } = useParams();
  const [loading, setLoading] = useState(true);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [profile, setProfile] = useState(null);

  // 获取当前登录用户
  const currentUser = JSON.parse(localStorage.getItem('current_user') || 'null');
  const isOwner = currentUser?.username === username;

  // 根据用户名动态设置网页标题
  useDocumentTitle(`${username} 的个人资料`, {
    prefix: '',
    suffix: ' - rtycn'
  });

  useEffect(() => {
    if (username) {
      fetchProfile();
    }
  }, [username]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await profileAPI.getProfile(username);
      setProfile(response.data);
    } catch (error) {
      message.error('获取用户资料失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      setAvatarUploading(true);
      const response = await profileAPI.uploadAvatar(username, formData);
      message.success('头像上传成功');
      fetchProfile();
      return false; // 阻止默认上传行为
    } catch (error) {
      message.error('头像上传失败');
      return false;
    } finally {
      setAvatarUploading(false);
    }
  };

  const uploadProps = {
    beforeUpload: handleAvatarUpload,
    showUploadList: false,
    accept: 'image/*',
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div style={{ width: '100%', maxWidth: '1320px', margin: '0 auto', background: '#fff', borderRadius: 8, padding: 24 }}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Title level={3}>用户资料不存在</Title>
          <Text type="secondary">该用户的资料信息不存在</Text>
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', maxWidth: '1320px', margin: '0 auto', background: '#fff', borderRadius: 8, padding: 24 }}>
      <Row gutter={[24, 24]}>
        {/* 左侧头像区域 */}
        <Col xs={24} sm={8} md={6} lg={4}>
          <Card style={{ textAlign: 'center' }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Avatar
                  size={128}
                  src={profile.avatar ? `${IMG_URL}${profile.avatar}` : undefined}
                  icon={<UserOutlined />}
                  style={{ marginBottom: 16 }}
                />
                <div>
                  {isOwner && currentUser && (
                    <Upload {...uploadProps}>
                      <Button
                        type="link"
                        icon={<UploadOutlined />}
                        size="small"
                        loading={avatarUploading}
                      >
                        更换头像
                      </Button>
                    </Upload>
                  )}
                </div>
              </div>
              <div>
                <Title level={4} style={{ marginBottom: 4 }}>{profile.username || username}</Title>
                <Text type="secondary">@{username}</Text>
              </div>
            </Space>
          </Card>
        </Col>

        {/* 右侧详细信息区域 */}
        <Col xs={24} sm={16} md={18} lg={20}>
          <Card
            title="个人资料"
            extra={
              isOwner && (
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => window.location.href = `/${username}/iden/manage/`}
                >
                  编辑资料
                </Button>
              )
            }
          >
            <Descriptions column={{ xs: 1, sm: 1, md: 2 }} bordered>
              <Descriptions.Item label="用户名">
                {profile.username || username}
              </Descriptions.Item>
              <Descriptions.Item label="昵称">
                {profile.nickname || '未设置'}
              </Descriptions.Item>
              <Descriptions.Item label="性别">
                {profile.gender === 'male' ? '男' : profile.gender === 'female' ? '女' : profile.gender === 'other' ? '其他' : '未设置'}
              </Descriptions.Item>
              <Descriptions.Item label="生日">
                <Space>
                  <CalendarOutlined />
                  {profile.birthday || '未设置'}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="所在地">
                <Space>
                  <HomeOutlined />
                  {profile.location || '未设置'}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={profile.status === 0 ? 'green' : 'default'}>
                  {profile.status === 0 ? '正常' : '异常'}
                </Tag>
              </Descriptions.Item>
            </Descriptions>

            {profile.bio && (
              <div style={{ marginTop: 24 }}>
                <Title level={5}>个人简介</Title>
                <Paragraph style={{ marginTop: 8 }}>
                  {profile.bio}
                </Paragraph>
              </div>
            )}

            {profile.website && (
              <div style={{ marginTop: 16 }}>
                <Title level={5}>个人网站</Title>
                <Text type="link" href={profile.website} target="_blank">
                  {profile.website}
                </Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Profile;
